import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import Form from "antd/es/form";
import Input from "antd/es/input";
import { useIntl } from "react-intl";

import ApiError from "@mapmycustomers/shared/util/api/ApiError";
import { Modal } from "@mapmycustomers/ui";

import PasswordValidation from "@app/component/auth/PasswordValidation";
import {
  getChangePasswordError,
  isChangePasswordLoading,
  isChangePasswordModalVisible,
} from "@app/store/iam";
import { changePassword, hideChangePasswordModal } from "@app/store/iam/actions";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import getValidationErrors from "@app/util/errorHandling/getValidationErrors";
import validatePasswords, {
  NOTHING_VALID,
  PasswordValidationResult,
} from "@app/util/validatePasswords";

import {
  useConfirmPasswordFieldValidationRules,
  useCurrentPasswordValidationRules,
  useNewPasswordFieldValidationRules,
} from "./validation-hooks";

const formItemLayout = {
  labelCol: { span: 12 },
};

interface Props {
  changePassword: typeof changePassword.request;
  error?: ApiError;
  hideChangePasswordModal: typeof hideChangePasswordModal;
  isCallLoading: boolean;
  isModalVisible: boolean;
}

const ChangePasswordModal: React.FC<Props> = ({
  changePassword,
  error,
  hideChangePasswordModal,
  isCallLoading,
  isModalVisible,
}) => {
  const intl = useIntl();
  const analyticIssuer = useAnalytics();
  const [form] = Form.useForm();

  useEffect(() => {
    // show error message depending on the error
    // TODO: add localized error message depending on the error code
    if (error) {
      const validationErrors = getValidationErrors(error);
      if (validationErrors.length) {
        form.setFields([
          { errors: [validationErrors[0].message], name: validationErrors[0].field },
        ]);
      }
    }
  }, [error, form]);

  const [canSubmit, setCanSubmit] = useState<boolean>(false);

  const currentPasswordValidationRules = useCurrentPasswordValidationRules(intl);
  const newPasswordFieldValidationRules = useNewPasswordFieldValidationRules(intl);
  const confirmPasswordFieldValidationRules = useConfirmPasswordFieldValidationRules(intl, form);

  const [passwordValidationResult, setPasswordValidationResult] =
    useState<PasswordValidationResult>(NOTHING_VALID);

  const handleFormFieldsChange = useCallback(() => {
    const passwordsValidationResult = validatePasswords(
      form.getFieldValue("password"),
      form.getFieldValue("confirm")
    );
    setPasswordValidationResult(passwordsValidationResult);
    setCanSubmit(
      passwordsValidationResult.isValid &&
        !form
          .getFieldsError()
          .some((field) => field.errors.length > 0 || !form.getFieldValue(field.name[0]))
    );
  }, [form, setCanSubmit]);

  const handleUpdatePassword = useCallback(() => {
    analyticIssuer?.clicked(["Change Password", "Change"]);
    const { currentPassword, password } = form.getFieldsValue();
    changePassword({ currentPassword, password });
  }, [analyticIssuer, changePassword, form]);

  return (
    <Modal
      cancelText={intl.formatMessage({
        id: "settings.profile.changePassword.modal.cancel",
        defaultMessage: "Cancel",
        description: "Cancel button title in the Change Password modal",
      })}
      destroyOnClose
      okButtonProps={{ disabled: !canSubmit, loading: isCallLoading }}
      okText={intl.formatMessage({
        id: "settings.profile.changePassword.modal.change",
        defaultMessage: "Change",
        description: "Change button title in the Change Password modal",
      })}
      onCancel={() => hideChangePasswordModal()}
      onOk={handleUpdatePassword}
      title={intl.formatMessage({
        id: "settings.profile.changePassword.modal.title",
        defaultMessage: "Change your password",
        description: "Title of the Change Password modal",
      })}
      visible={isModalVisible}
    >
      <Form form={form} layout="vertical" onFieldsChange={handleFormFieldsChange} preserve={false}>
        <Form.Item
          {...formItemLayout}
          label={intl.formatMessage({
            id: "settings.profile.changePassword.modal.currentPassword",
            defaultMessage: "Current Password",
            description: "Current Password field name in the Change Password modal",
          })}
          name="currentPassword"
          rules={currentPasswordValidationRules}
        >
          <Input.Password autoComplete="current-password" spellCheck="false" />
        </Form.Item>
        <Form.Item
          {...formItemLayout}
          label={intl.formatMessage({
            id: "settings.profile.changePassword.modal.newPassword",
            defaultMessage: "New Password",
            description: "New Password field name in the Change Password modal",
          })}
          name="password"
          rules={newPasswordFieldValidationRules}
        >
          <Input.Password autoComplete="new-password" spellCheck="false" />
        </Form.Item>
        <Form.Item
          {...formItemLayout}
          dependencies={["password"]}
          label={intl.formatMessage({
            id: "settings.profile.changePassword.modal.confirmPassword",
            defaultMessage: "Confirm Password",
            description: "Confirm Password field name in the Change Password modal",
          })}
          name="confirm"
          rules={confirmPasswordFieldValidationRules}
        >
          <Input.Password autoComplete="new-password" spellCheck="false" />
        </Form.Item>
        <Form.Item>
          <PasswordValidation validationResult={passwordValidationResult} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  error: getChangePasswordError(state),
  isCallLoading: isChangePasswordLoading(state),
  isModalVisible: isChangePasswordModalVisible(state),
});

const mapDispatchToProps = {
  changePassword: changePassword.request,
  hideChangePasswordModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(ChangePasswordModal);
